import React, { useState } from "react";
import { Box, Typography, InputBase, Chip, CircularProgress, makeStyles } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import WarningAmberIcon from "@material-ui/icons/Warning";
import { useVehicleIndicators } from "./useVehicleIndicators";
import { Vehicle } from "./types";

// Converted from nuvven-b2b-app's sx-based (MUI v5) design to v4's makeStyles — see
// TrackingHistoryBar.tsx for why literal pixels are used instead of theme.spacing().
const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#f9fafb" },
  header: { backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 24px", flexShrink: 0 },
  titleBlock: { marginBottom: 12 },
  title: { fontWeight: 600, color: "#111827" },
  subtitle: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
    maxWidth: 360,
  },
  searchIcon: { fontSize: 16, color: "#9ca3af" },
  searchInput: { fontSize: 13, flex: 1 },
  list: { flex: 1, overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 },
  loadingRow: { display: "flex", justifyContent: "center", padding: "32px 0" },
  emptyMessage: { fontSize: 12, color: "#9ca3af", textAlign: "center", marginTop: 16 },
  card: { backgroundColor: "#fff", borderRadius: 12, border: "1px solid #f3f4f6", boxShadow: theme.shadows[1], overflow: "hidden" },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: "1px solid #fef3c7",
    backgroundColor: "rgba(254,243,199,0.4)",
  },
  cardHeaderLeft: { display: "flex", alignItems: "center", gap: 12 },
  cardIcon: { fontSize: 16, color: "#9ca3af" },
  plate: { fontWeight: 600, fontSize: 13, color: "#111827" },
  makeModel: { fontSize: 12, color: "#9ca3af", marginLeft: 8 },
  activeChip: { backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 500 },
  indicatorRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 16px" },
  indicatorRowBordered: { borderTop: "1px solid #f9fafb" },
  warningIcon: { fontSize: 14, color: "#d97706", flexShrink: 0 },
  indicatorLabel: { fontSize: 12, color: "#4b5563" },
}));

interface IndicatorsPageProps {
  vehicles: Vehicle[];
  loading: boolean;
}

export function IndicatorsPage({ vehicles, loading }: IndicatorsPageProps) {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const trackedVehicles = vehicles.filter((v) => !!v.telemetryDeviceId);
  const { indicatorsByEsn, loading: indicatorsLoading } = useVehicleIndicators(trackedVehicles.map((v) => v.telemetryDeviceId));

  // Only vehicles with at least one active dashboard warning are shown — no "All Clear" filler rows.
  const withIndicators = trackedVehicles.filter((v) => (indicatorsByEsn[v.telemetryDeviceId]?.length ?? 0) > 0);
  const filtered = withIndicators.filter(
    (v) => v.plate.toLowerCase().includes(search.toLowerCase()) || v.make.toLowerCase().includes(search.toLowerCase())
  );
  const totalActive = trackedVehicles.reduce((n, v) => n + (indicatorsByEsn[v.telemetryDeviceId]?.length ?? 0), 0);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleBlock}>
          <Typography className={classes.title}>Vehicle Indicators</Typography>
          <Typography className={classes.subtitle}>
            {totalActive} active dashboard warning{totalActive === 1 ? "" : "s"} in the last 24 hours · vehicles without a telemetry device aren't shown
          </Typography>
        </Box>
        <Box className={classes.searchBox}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase placeholder="Search vehicle…" value={search} onChange={(e) => setSearch(e.target.value)} className={classes.searchInput} />
        </Box>
      </Box>

      <Box className={classes.list}>
        {(loading || indicatorsLoading) && (
          <Box className={classes.loadingRow}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && !indicatorsLoading && filtered.length === 0 && (
          <Typography className={classes.emptyMessage}>
            {trackedVehicles.length === 0 ? "No vehicles with a telemetry device found." : "No active dashboard warnings found."}
          </Typography>
        )}

        {filtered.map((v) => {
          const active = indicatorsByEsn[v.telemetryDeviceId] ?? [];
          return (
            <Box key={v.id} className={classes.card}>
              <Box className={classes.cardHeader}>
                <Box className={classes.cardHeaderLeft}>
                  <DirectionsCarIcon className={classes.cardIcon} />
                  <Box>
                    <Typography component="span" className={classes.plate}>{v.plate}</Typography>
                    <Typography component="span" className={classes.makeModel}>{v.make} {v.model}</Typography>
                  </Box>
                </Box>
                <Chip label={`${active.length} active`} size="small" className={classes.activeChip} />
              </Box>
              {active.map((ind, i) => (
                <Box key={`${ind.field}-${i}`} className={`${classes.indicatorRow} ${i > 0 ? classes.indicatorRowBordered : ""}`}>
                  <WarningAmberIcon className={classes.warningIcon} />
                  <Typography className={classes.indicatorLabel}>{ind.label}</Typography>
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
